import type { LeadRequest } from "@component/assets/AbstractLeadForm/types";
import { getBankName } from "@component/assets/data/banks";
import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import { logError } from "@utils/sentry-error";
import autobind from "autobind-decorator";
import type { ReactNode } from "react";
import React from "react";
import { INIT_VALUES, calculate, getLeadCalculatorResponse, searchParamsToRequest } from "../Calculator/domain";
import type { RetailAccountResponseItem, RetailAccountSearchParams } from "../Calculator/types";
import type {
	RetailAccountLeadFormFields,
	RetailAccountLeadFormProps,
	RetailAccountLeadFormState,
} from "../LeadForm/RetailAccountLeadForm";
import { RetailAccountLeadForm } from "../LeadForm/RetailAccountLeadForm";
import type { RetailAccountCalcRequest, RetailAccountCalcResponse } from "../LeadForm/types";
import * as Step from "./Steps";
import "./style.scss";

export interface RetailAccountSmallCalculatorFormFields extends RetailAccountLeadFormFields {
	// Kalkulációhoz
	receiptOfMoneyAmount: number;
	internetTransferAmount: number;
	internetTransferCount: number;
	cashWithdrawalOwnAtmAmount: number;
	cashWithdrawalOwnAtmCount: number;
}

export type ProductVariant = "cib-eco-lead" | "unicredit" | "raiffeisen" | "erste" | "cib-eco-plus";

export interface RetailAccountSmallCalculatorProps extends RetailAccountLeadFormProps {
	variant?: undefined | "compare-free-retail-account" | ProductVariant;
}

export interface RetailAccountSmallCalculatorState<
	FormFields extends RetailAccountLeadFormFields = RetailAccountLeadFormFields,
> extends RetailAccountLeadFormState<FormFields> {
	results: RetailAccountResponseItem[];
	selected: RetailAccountCalcResponse;
	selectedPosition: number;
}

/**
 * @Shortcode
 *
 * @title "Bankszámla kalkulátor"
 * @shortcode "bankaccounts_small"
 * @slug "bankszamla"
 * @type "calculator"
 * @propsList "[{"variant":"cib-eco-lead"}]"
 */
export class RetailAccountSmallCalculator<
	FormFields extends RetailAccountSmallCalculatorFormFields = RetailAccountSmallCalculatorFormFields,
	Props extends RetailAccountSmallCalculatorProps = RetailAccountSmallCalculatorProps,
	State extends RetailAccountSmallCalculatorState<FormFields> = RetailAccountSmallCalculatorState<FormFields>,
> extends RetailAccountLeadForm<FormFields, Props, State> {
	// EventParams
	protected readonly productTypes: string = "RETAIL_ACCOUNT";
	protected readonly componentType: string = "small-calculator";

	public static defaultProps = {
		title: "Igénylés indítása!",
		outsideTitle: "Bankszámla kalkulátor",
		outsideTitleLink: "/bankszamla/",
		bottomLogo: true,
	};

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			activeStep: "start-screen",
			results: [],
			selected: undefined,
			selectedPosition: undefined,
		};
	}

	protected renderSteps(): ReactNode {
		const { variant } = this.props;
		const { submitState, results } = this.state;

		return (
			<>
				<Step.StartScreen stepName="start-screen" defaultTarget="step-1" variant={variant} />
				<Step.Step1 stepName="step-1" defaultTarget="results" submitState={submitState} variant={variant} />
				<Step.Result
					stepName="results"
					defaultTarget="lead-form"
					results={results}
					onSelect={this.onSelectProduct}
					variant={variant}
					canStepBack
				/>
				{super.renderSteps()}
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		const { values } = this.state;

		if (target !== "results") {
			super.onStep(target, withEvent);

			return;
		}

		this.onStepResults(values)
			.then(() => {
				super.onStep(target, withEvent);
				this.setState({
					submitState: undefined,
				});
			})
			.catch((error: Error) => {
				this.setState({
					submitState: "error",
				});

				this.promisePool.addTimeout().then(() => {
					this.setState({
						submitState: undefined,
					});
				});

				logError(error);
			});
	}

	protected async onStepResults(contactData: RetailAccountSmallCalculatorFormFields): Promise<void> {
		this.setState({
			submitState: "loading",
		});

		return this.calculate(contactData);
	}

	@autobind
	private onSelectProduct(index: number, result: RetailAccountResponseItem): void {
		this.setState({
			selected: getLeadCalculatorResponse(result),
			selectedPosition: index + 1,
		});

		this.sendEvent("BM_resultItemButton", {
			buttonId: GoogleAnalytics4.generateId("bi"),
			position: index + 1,
			buttonName: `detail`,
			providerName: result.bank,
			productName: result.name,
			productId: result.id,
		});
	}

	protected getProductName(): string {
		const { selected } = this.state;
		const { response = selected } = this.props;

		return response?.productName;
	}

	protected getBankCode(): string {
		const { selected } = this.state;
		const { response = selected } = this.props;

		return response?.bankCode;
	}

	protected getLeadData(): LeadRequest<RetailAccountCalcRequest, RetailAccountCalcResponse> {
		const { selected } = this.state;
		const data = super.getLeadData();

		if (selected) {
			data.response = {
				...data.response,
				...selected,
			};
		}

		return data;
	}

	protected async calculate(data: RetailAccountSmallCalculatorFormFields): Promise<void> {
		return calculate(searchParamsToRequest(this.getSearchData(data), "FIRST_YEAR_MONTHLY_TOTAL_FEE")).then(
			({ normalList }) => {
				const results = normalList.filter((item) => !item.marketing?.highlight).slice(0, 4);

				this.setState({
					results,
				});

				this.sendCalculationEvent();
			},
		);
	}

	protected getSearchData(data: RetailAccountSmallCalculatorFormFields): RetailAccountSearchParams {
		const {
			receiptOfMoneyAmount,
			internetTransferAmount,
			internetTransferCount,
			cashWithdrawalOwnAtmAmount,
			cashWithdrawalOwnAtmCount,
		} = data;

		return {
			...INIT_VALUES,
			receiptOfMoneyAmount,
			internetTransferAmount,
			internetTransferCount,
			cashWithdrawalOwnAtmAmount,
			cashWithdrawalOwnAtmCount,
		};
	}

	protected getCalculationData(): string {
		const { searchParams, request } = this.props;
		const { values } = this.state;

		const data = searchParamsToRequest(
			{
				...this.getSearchData(values),
				...request,
				...searchParams,
			},
			undefined,
		);

		return JSON.stringify({
			...data,
			bankName: getBankName(data?.bank),
		});
	}

	protected getCalculatorResponse(): RetailAccountCalcResponse {
		const { selected } = this.state;

		return selected || super.getCalculatorResponse();
	}

	protected getClassName(): string {
		const { variant } = this.props;

		if (variant) {
			return `${super.getClassName()} ${variant}`;
		}

		return super.getClassName();
	}

	protected getComponentName(): string {
		return `${super.getComponentName()}.RetailAccountSmallCalculator`;
	}
}

export default RetailAccountSmallCalculator;
